import { useState, useMemo, useEffect, useRef } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { SmartImage } from "@/components/ui/SmartImage"
import { Newspaper, Megaphone } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { t } from "@/lib/i18n"
import { useLanguage } from "@/lib/lang"
// Hindi QA imports removed
import { TabbedNews } from "@/components/ui/TabbedNews"
import { HomeHero } from "@/components/HomeHero"
import { PanchayatSelector } from "@/components/PanchayatSelector"
import { useAuth } from "@/contexts/AuthContext"

const PERF_DEBUG_SESSION_ID = "slow-page-load"

function reportPerfDebug(hypothesisId: string, location: string, msg: string, data: Record<string, unknown>) {
  if (!(import.meta as any)?.env?.DEV) return
  if (typeof window === "undefined" || (window as any).__TRAE_PERF_DEBUG__ !== true) return
  if (typeof fetch !== "function") return
  const perfDebugUrl = String((window as any).__TRAE_LOCAL_DEBUG_URL__ || "").trim()
  if (!perfDebugUrl) return
  fetch(perfDebugUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sessionId: PERF_DEBUG_SESSION_ID,
      runId: "pre-fix",
      hypothesisId,
      location,
      msg,
      data,
      ts: Date.now(),
    }),
  }).catch(() => { })
}

export default function HaryanaPanchayatPortal() {
  const { lang } = useLanguage()
  const { currentUser } = useAuth()
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    if (typeof window === "undefined") return
    const update = () => setIsMobile(window.innerWidth <= 640)
    update()
    window.addEventListener("resize", update)
    return () => window.removeEventListener("resize", update)
  }, [])
  const welcomeTitle = t("welcome_title", lang)
  const [homeViewMode, setHomeViewMode] = useState<'saved' | 'selector'>(() => {
    try {
      const v = localStorage.getItem('home_view_mode')
      return v === 'saved' ? 'saved' : 'selector'
    } catch {
      return 'selector'
    }
  })
  const [savedProfileGp, setSavedProfileGp] = useState<{ district: string; block: string; village: string } | null>(null)
  // Hindi QA state removed

  // Carousel: images and state
  const IMAGE_DISPLAY_MS = 4000
  const LOGO_DISPLAY_MS = 2000
  const FADE_DURATION_S = 0.4
  const LONG_PRESS_MS = Number(import.meta.env.VITE_CAROUSEL_LONG_PRESS_MS ?? 500)
  const VIBRATE_ON_PAUSE = (import.meta.env.VITE_CAROUSEL_VIBRATE ?? "true") === "true"
  const objectFitMode: "contain" | "cover" = isMobile ? "cover" : "contain"
  // Responsive image sizes mapping for srcset width descriptors
  const responsiveSizes = "(max-width: 600px) 480px, (max-width: 1024px) 800px, 1200px"
  const buildSrcSets = (src: string) => {
    // Expect src like /images/pr1.jpg; build variant names alongside original
    const baseName = src.split("/").pop()?.replace(/\.(jpg|jpeg)$/i, "") || "image"
    const jpgSrcSet = [`/images/${baseName}-480.jpg 480w`, `/images/${baseName}-800.jpg 800w`, `/images/${baseName}-1200.jpg 1200w`].join(", ")
    const webpSrcSet = [`/images/${baseName}-480.webp 480w`, `/images/${baseName}-800.webp 800w`, `/images/${baseName}-1200.webp 1200w`].join(", ")
    return { jpgSrcSet, webpSrcSet }
  }
  const carouselImages = useMemo(
    () => [
      { src: "/images/pr1.jpg", altKey: "carousel_alt_1" },
      { src: "/images/pr2.jpg", altKey: "carousel_alt_2" },
      { src: "/images/pr3.jpg", altKey: "carousel_alt_3" },
      { src: "/images/pr4.jpg", altKey: "carousel_alt_4" },
      { src: "/images/pr5.jpg", altKey: "carousel_alt_5" },
      { src: "/images/pr6.jpg", altKey: "carousel_alt_6" },
      { src: "/images/pr7.jpg", altKey: "carousel_alt_7" },
      { src: "/images/pr8.jpg", altKey: "carousel_alt_8" },
      { src: "/images/pr9.jpg", altKey: "carousel_alt_9" },
      { src: "/images/pr10.jpg", altKey: "carousel_alt_10" },
      { src: "/images/pr11.jpg", altKey: "carousel_alt_11" },
      { src: "/images/pr12.jpg", altKey: "carousel_alt_12" },
    ],
    []
  )
  const [carouselIndex, setCarouselIndex] = useState(0)
  const [carouselPhase, setCarouselPhase] = useState<"image" | "logo">("image")
  const [failedImages, setFailedImages] = useState<Record<number, boolean>>({})
  const [logoSlideSrc, setLogoSlideSrc] = useState("/images/logodpm.png")
  const [aspectByIndex, setAspectByIndex] = useState<Record<number, number>>({})
  const [currentAspect, setCurrentAspect] = useState<number>(16 / 9)
  const carouselViewportRef = useRef<HTMLDivElement | null>(null)
  const [carouselHeightPx, setCarouselHeightPx] = useState<number | null>(null)
  const [isPaused, setIsPaused] = useState(false)
  const [showPauseOverlay, setShowPauseOverlay] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const startRef = useRef<number>(Date.now())
  const remainingRef = useRef<number>(IMAGE_DISPLAY_MS)
  const pauseSourceRef = useRef<"hover" | "focus" | "touch" | "visibility" | null>(null)
  const longPressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const touchActiveRef = useRef(false)
  const lastAdvanceRef = useRef<number>(Date.now())
  const carouselIndexRef = useRef(0)
  const phaseRef = useRef<"image" | "logo">("image")
  const pausedRef = useRef(false)
  const pendingIndexRef = useRef<number | null>(null)
  const touchStartXRef = useRef<number | null>(null)
  const touchStartYRef = useRef<number | null>(null)

  useEffect(() => {
    carouselIndexRef.current = carouselIndex
  }, [carouselIndex])

  useEffect(() => {
    const known = aspectByIndex[carouselIndex]
    if (known && Number.isFinite(known)) {
      setCurrentAspect((prev) => (Math.abs(prev - known) > 0.001 ? known : prev))
    }
  }, [aspectByIndex, carouselIndex])

  useEffect(() => {
    if (!isMobile) {
      setCarouselHeightPx(null)
      return
    }
    const el = carouselViewportRef.current
    if (!el) return
    const padding = 8
    const fit = () => {
      const width = el.getBoundingClientRect().width || 0
      const ratio = Number.isFinite(currentAspect) && currentAspect > 0 ? currentAspect : 16 / 9
      const innerWidth = Math.max(0, width - padding * 2)
      const innerHeight = ratio ? innerWidth / ratio : 0
      const totalHeight = Math.max(0, innerHeight + padding * 2)
      setCarouselHeightPx(totalHeight ? Math.round(totalHeight) : null)
    }
    const ro = new ResizeObserver(fit)
    ro.observe(el)
    fit()
    return () => ro.disconnect()
  }, [isMobile, currentAspect])

  useEffect(() => {
    phaseRef.current = carouselPhase
  }, [carouselPhase])

  useEffect(() => {
    pausedRef.current = isPaused
  }, [isPaused])

  const phaseDurationMs = (phase: "image" | "logo") => (phase === "image" ? IMAGE_DISPLAY_MS : LOGO_DISPLAY_MS)

  const clearTimer = () => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }

  const scheduleNext = (delayMs: number) => {
    clearTimer()
    startRef.current = Date.now()
    remainingRef.current = delayMs
    timeoutRef.current = setTimeout(() => {
      if (pausedRef.current) return
      if (phaseRef.current === "image") {
        pendingIndexRef.current = (carouselIndexRef.current + 1) % carouselImages.length
        setCarouselPhase("logo")
        lastAdvanceRef.current = Date.now()
        scheduleNext(LOGO_DISPLAY_MS)
        return
      }
      setCarouselIndex((i) => {
        const nextIdx = pendingIndexRef.current ?? (i + 1) % carouselImages.length
        pendingIndexRef.current = null
        return nextIdx
      })
      setCarouselPhase("image")
      lastAdvanceRef.current = Date.now()
      scheduleNext(IMAGE_DISPLAY_MS)
    }, delayMs)
  }

  // lang persistence handled by provider

  useEffect(() => {
    scheduleNext(IMAGE_DISPLAY_MS)
    return () => clearTimer()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const activatePause = (source: "hover" | "focus" | "touch" | "visibility") => {
    pauseSourceRef.current = source
    if (!pausedRef.current) {
      setIsPaused(true)
      const elapsed = Date.now() - startRef.current
      remainingRef.current = Math.max(phaseDurationMs(phaseRef.current) - elapsed, 0)
      clearTimer()
    }
  }
  const releasePause = (source: "hover" | "focus" | "touch" | "visibility") => {
    if (pauseSourceRef.current !== source) return
    pauseSourceRef.current = null
    if (pausedRef.current) {
      setIsPaused(false)
      scheduleNext(remainingRef.current || phaseDurationMs(phaseRef.current))
    }
  }

  const handleMouseEnter = () => activatePause("hover")
  const handleMouseLeave = () => releasePause("hover")

  // Touch long-press handlers for parity pause
  const handleTouchStart = (e: React.TouchEvent) => {
    touchActiveRef.current = true
    if (e.touches && e.touches.length === 1) {
      touchStartXRef.current = e.touches[0].clientX
      touchStartYRef.current = e.touches[0].clientY
    } else {
      touchStartXRef.current = null
      touchStartYRef.current = null
    }
    if (longPressTimeoutRef.current != null) clearTimeout(longPressTimeoutRef.current)
    longPressTimeoutRef.current = setTimeout(() => {
      if (!touchActiveRef.current) return
      activatePause("touch")
      setShowPauseOverlay(true)
      try {
        if (VIBRATE_ON_PAUSE && navigator?.vibrate) navigator.vibrate(10)
      } catch { }
    }, LONG_PRESS_MS)
  }
  const handleTouchEnd = (e: React.TouchEvent) => {
    touchActiveRef.current = false
    if (longPressTimeoutRef.current != null) clearTimeout(longPressTimeoutRef.current)
    longPressTimeoutRef.current = null
    setShowPauseOverlay(false)

    const sx = touchStartXRef.current
    const sy = touchStartYRef.current
    const endTouch = e.changedTouches && e.changedTouches.length > 0 ? e.changedTouches[0] : null
    if (sx != null && sy != null && endTouch && pauseSourceRef.current !== "touch") {
      const dx = endTouch.clientX - sx
      const dy = endTouch.clientY - sy
      const absDx = Math.abs(dx)
      const absDy = Math.abs(dy)
      if (absDx >= 50 && absDx >= absDy * 1.2) {
        if (dx < 0) nextSlide()
        else prevSlide()
      }
    }
    touchStartXRef.current = null
    touchStartYRef.current = null
    releasePause("touch")
  }
  const handleTouchCancel = (e: React.TouchEvent) => handleTouchEnd(e)

  // Prefetch current and next images; mark failures for fallback
  useEffect(() => {
    const curr = new Image()
    curr.src = carouselImages[carouselIndex].src
    curr.onerror = () => setFailedImages((prev) => ({ ...prev, [carouselIndex]: true }))
    const nextIdx = pendingIndexRef.current ?? (carouselIndex + 1) % carouselImages.length
    const next = new Image()
    next.src = carouselImages[nextIdx].src
    next.onerror = () => setFailedImages((prev) => ({ ...prev, [nextIdx]: true }))
  }, [carouselIndex, carouselImages])

  const showLogoThenGoTo = (targetIndex: number) => {
    pendingIndexRef.current = targetIndex
    setCarouselPhase("logo")
    lastAdvanceRef.current = Date.now()
    scheduleNext(LOGO_DISPLAY_MS)
  }

  const prevSlide = () => {
    const target = (carouselIndexRef.current - 1 + carouselImages.length) % carouselImages.length
    showLogoThenGoTo(target)
  }
  const nextSlide = () => {
    const target = (carouselIndexRef.current + 1) % carouselImages.length
    showLogoThenGoTo(target)
  }
  const goToSlide = (idx: number) => {
    showLogoThenGoTo(idx)
  }

  // Pause when tab hidden; resume when visible, keeping remaining time
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        activatePause("visibility")
      } else {
        releasePause("visibility")
      }
    }
    document.addEventListener("visibilitychange", onVis)
    return () => document.removeEventListener("visibilitychange", onVis)
  }, [])

  useEffect(() => {
    // #region debug-point E:home-map-load-skipped
    reportPerfDebug("E", "src/components/HaryanaPanchayatPortal.tsx:308", "[DEBUG] Home page skipped eager panchayat map hydration", {
      reason: "homepage_does_not_require_full_dataset",
    })
    // #endregion
  }, [])

  useEffect(() => {
    document.title = t("title", lang)
  }, [lang])

  const loadSavedProfileGp = async (userId?: string) => {
    if (!userId) {
      try {
        const s = localStorage.getItem('user_profile')
        if (s) {
          const parsed = JSON.parse(s) as { district?: string; block?: string; village?: string }
          setSavedProfileGp({
            district: parsed.district || '',
            block: parsed.block || '',
            village: parsed.village || '',
          })
        }
      } catch {
      }
      return
    }
    return
  }

  useEffect(() => {
    const onMode = (e: Event) => {
      const ev = e as CustomEvent<{ mode?: 'saved' | 'selector' }>
      if (ev.detail?.mode === 'saved' || ev.detail?.mode === 'selector') {
        setHomeViewMode(ev.detail.mode)
      }
    }
    window.addEventListener('home:view-mode', onMode as EventListener)
    return () => {
      window.removeEventListener('home:view-mode', onMode as EventListener)
    }
  }, [])

  useEffect(() => {
    try {
      const v = localStorage.getItem('home_view_mode')
      setHomeViewMode(v === 'saved' ? 'saved' : 'selector')
    } catch {
    }
  }, [])

  useEffect(() => {
    loadSavedProfileGp(currentUser?.id)
    const onProfileUpdated = () => {
      loadSavedProfileGp(currentUser?.id)
    }
    window.addEventListener('profile:updated', onProfileUpdated)
    return () => {
      window.removeEventListener('profile:updated', onProfileUpdated)
    }
  }, [currentUser?.id])

  const savedGpLine = savedProfileGp?.village && savedProfileGp?.block && savedProfileGp?.district
    ? `${savedProfileGp.village}, ${savedProfileGp.block}, ${savedProfileGp.district}`
    : null

  // Language initialization and persistence handled globally

  // Hindi QA logging removed

  // Hindi QA action removed

  // Selection controls removed; dependent memoized options and handlers deleted to reduce unused code

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-emerald-100" lang={lang}>
      {/* Header moved to global SiteHeader */}

      <HomeHero />

      {/* State Overview */}
      <section className="p-6 text-center">
        <h2
          className={[
            "welcome-title-fluid font-bold text-green-800 mb-4 text-center",
            isMobile ? "leading-[1.2]" : "text-3xl leading-[1.32]",
          ].join(" ")}
        >
          <span className="inline-block whitespace-nowrap">{welcomeTitle}</span>
        </h2>
        <p className="text-gray-700 max-w-3xl mx-auto mb-6">
          {t("welcome_body", lang)}
        </p>

        {/* Selection controls removed per current requirements */}


        {/* Image Carousel */}
        <section className="px-6">
          <div className="max-w-6xl mx-auto">
            <div
              className="relative rounded-lg overflow-hidden shadow bg-white"
              data-testid="home-carousel"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchCancel}
            >
              <div
                className="w-full home-carousel-viewport"
                ref={carouselViewportRef}
                style={
                  isMobile
                    ? { height: carouselHeightPx ? `${carouselHeightPx}px` : undefined }
                    : { aspectRatio: String(currentAspect || 1.7777777778) }
                }
              >
                <AnimatePresence mode="sync">
                  <motion.div
                    key={`${carouselIndex}-${carouselPhase}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: FADE_DURATION_S }}
                    className={`absolute inset-0${isMobile ? " p-2" : ""}`}
                    data-testid="home-carousel-slide"
                    data-slide-kind={carouselPhase}
                    data-slide-index={carouselIndex}
                    data-paused={isPaused}
                    style={{ willChange: "opacity" }}
                  >
                    {carouselPhase === "logo" ? (
                      <div className="w-full h-full bg-white flex items-center justify-center">
                        <img
                          data-testid="home-carousel-logo"
                          src={logoSlideSrc}
                          alt="Digital Panchayat Mission"
                          className="w-auto max-w-full object-contain"
                          style={{ maxHeight: isMobile ? "60%" : "100%" }}
                          loading="eager"
                          decoding="async"
                          onError={() => setLogoSlideSrc("/images/placeholder.svg")}
                        />
                      </div>
                    ) : failedImages[carouselIndex] ? (
                      <img
                        src="/images/placeholder.svg"
                        alt={t(carouselImages[carouselIndex].altKey, lang)}
                        className={`w-full h-full ${isMobile ? "object-cover" : "object-contain"}`}
                      />
                    ) : (
                      (() => {
                        const { jpgSrcSet, webpSrcSet } = buildSrcSets(carouselImages[carouselIndex].src)
                        return (
                          <SmartImage
                            src={carouselImages[carouselIndex].src}
                            alt={t(carouselImages[carouselIndex].altKey, lang)}
                            lazy
                            priority={false}
                            decoding="async"
                            sizes={responsiveSizes}
                            srcSet={jpgSrcSet}
                            sources={[
                              { srcSet: webpSrcSet, type: "image/webp" },
                              { srcSet: jpgSrcSet, type: "image/jpeg" },
                            ]}
                            className={`w-full h-full ${isMobile ? "rounded-md" : ""}`}
                            objectFit={objectFitMode}
                            autoAspectRatio={!isMobile}
                            onError={() => setFailedImages((prev) => ({ ...prev, [carouselIndex]: true }))}
                            onLoad={(e) => {
                              const img = e.currentTarget as HTMLImageElement
                              const nw = img.naturalWidth || 0
                              const nh = img.naturalHeight || 0
                              if (nw > 0 && nh > 0) {
                                const ratio = nw / nh
                                setAspectByIndex((prev) => ({ ...prev, [carouselIndex]: ratio }))
                                setCurrentAspect((prevAspect) => (Math.abs(prevAspect - ratio) > 0.001 ? ratio : prevAspect))
                              }
                              setFailedImages((prev) => {
                                const next = { ...prev }
                                delete next[carouselIndex]
                                return next
                              })
                            }}
                          />
                        )
                      })()
                    )}
                    {showPauseOverlay && (
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 bg-black/10 ring-2 ring-green-500"
                      />
                    )}
                  </motion.div>
                </AnimatePresence>

                {/* Navigation Controls */}
                <div className="absolute inset-y-0 left-0 flex items-center">
                  <Button
                    variant="secondary"
                    className="bg-white/70 hover:bg-white text-gray-800 px-2 py-1 m-2 rounded"
                    onClick={prevSlide}
                    onFocus={() => activatePause("focus")}
                    onBlur={() => releasePause("focus")}
                    aria-label={lang === "hi" ? "पिछला चित्र" : "Previous image"}
                  >
                    ‹
                  </Button>
                </div>
                <div className="absolute inset-y-0 right-0 flex items-center">
                  <Button
                    variant="secondary"
                    className="bg-white/70 hover:bg-white text-gray-800 px-2 py-1 m-2 rounded"
                    onClick={nextSlide}
                    onFocus={() => activatePause("focus")}
                    onBlur={() => releasePause("focus")}
                    aria-label={lang === "hi" ? "अगला चित्र" : "Next image"}
                  >
                    ›
                  </Button>
                </div>
              </div>
            </div>
            {/* Dots */}
            <div className="mt-3 flex justify-center gap-2">
              {carouselImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToSlide(idx)}
                  onFocus={() => activatePause("focus")}
                  onBlur={() => releasePause("focus")}
                  aria-label={(lang === "hi" ? "स्लाइड " : "Slide ") + (idx + 1)}
                  className={`w-2.5 h-2.5 rounded-full ${idx === carouselIndex ? "bg-green-700" : "bg-green-300"} transition-colors`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Secondary Banner */}
        <div className="banner-secondary mt-8 py-4">
          <h2 className="text-xl md:text-2xl font-bold text-center">
            {t("level_header_tagline", lang)}
          </h2>
        </div>

        {/* Selection Controls - Relocated below Carousel */}
        {homeViewMode === 'saved' && (
          <div className="px-6 mb-4">
            <div className="max-w-6xl mx-auto">
              <div className="border rounded-md p-4 bg-white shadow-sm">
                <div className="text-xs text-gray-600 mb-1">Home Gram Panchayat (saved in profile)</div>
                <div className="text-lg font-semibold text-green-800">{savedGpLine || 'Not Set'}</div>
                <div className="text-xs text-gray-500 mt-1">Press the Home icon again to change.</div>
              </div>
            </div>
          </div>
        )}

        <PanchayatSelector
          mode="navigate"
          lockedGp={null}
          storageKey="home_gp_selection"
        />

        {/* Hindi QA summary removed */}
      </section>

      {/* Latest Updates Title */}
      <div className="home-updates-ad-rail">
        <div className="mb-2 flex items-center gap-2">
          <h2 className="text-2xl md:text-3xl font-bold text-green-800 flex items-center gap-2">
            <Newspaper /> {t("latest_updates_title", lang)}
          </h2>
        </div>
        <div className="border-b border-green-200 mb-4" />
      </div>

      {/* Notification & Advertisement Section */}
      <section className="pb-6">
        <div className="home-updates-ad-rail home-updates-ad-grid">
          <motion.div whileHover={{ scale: 1.02 }} className="home-updates-ad-box">
            <TabbedNews
              lang={lang}
              schemeItems={[
                "Mahatma Gandhi National Rural Employment Guarantee Scheme (MGNREGS)",
                "National Rural Livelihoods Mission (NRLM)/Aajeevika",
                "Deen Dayal Upadhaya- Gramin Kaushal Yojana (DDU-GKY)",
                "Rural Self Employment Training Institutes (RSETIs)",
                "Pradhan Mantri Krishi Sinchayee Yojana erstwhile-Watershed Management Programme (IWMP/ Now PMKSY)",
                "Member of Parliament Local Area Development Scheme (MPLADS)",
                "Vidhayak Adarsh Nagar Avam Gram Yojana (VANGY)",
                "Pradhan Mantri Adarsh Gram Yojana (PMAGY)",
                "Saansad Adarsh Gram Yojana (SAGY)",
                "Shyama Prasad Mukherji Rurban Mission (SPMRM)",
                "Swaranjayanti Khand Utthan Yojana (SKUY)",
                "Matching Grant Scheme",
                "Haryana Rural Development Authority (HRDA)",
                "Haryana Swaran Jayanti Mahagram Yojna (SMAGY)",
                "Mahatama Gandhi Gramin Basti Yojna Scheme (MGGBYS)",
                "Haryana Gramin Vikas Yojana (HGVY)",
                "Deenbandhu Gram Uday Yojana Scheme",
                "Swachh Bharat Mission-Gramin (SBM-G)",
                "Pradhan Mantri Jan Vikas Karyakram (PMJVK)",
                "Start-up Village Entrepreneurship Programme (SVEP)",
              ]}
            />
          </motion.div>

          <motion.div whileHover={{ scale: 1.02 }} className="home-updates-ad-box">
            <Card className="bg-yellow-100 shadow-lg">
              <CardContent className="p-4 text-center">
                <h3 className="text-lg font-bold text-gray-800 flex items-center justify-center gap-2 mb-2"><Megaphone /> {t("ad_title", lang)}</h3>
                <p className="text-gray-600">
                  {t("ad_body", lang)}
                  {" "}
                  <a
                    href="mailto:techmaestrodigital@gmail.com"
                    className="font-medium underline underline-offset-2 hover:text-green-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 focus-visible:ring-offset-yellow-100 rounded"
                  >
                    techmaestrodigital@gmail.com
                  </a>
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Haryana Overview */}
      <section className="bg-green-50 py-10">
        <div className={["max-w-5xl text-center", isMobile ? "mx-4" : "mx-auto px-4 sm:px-0"].join(" ")}>
          <h3 className="text-2xl font-bold text-green-800 mb-4">{t("dashboard_title", lang)}</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-gray-800">
            {/* 1. Zila Parishad */}
            <Card className={`shadow-md ${isMobile ? "w-3/4 mx-auto" : ""}`}>
              <CardContent className={`${isMobile ? "p-3 h-[96px] flex flex-col items-center justify-center" : "p-4"} text-center`}>
                <h4 className="font-semibold">{t("level_zp", lang)}</h4>
                <p className="text-2xl font-bold text-green-700">22</p>
              </CardContent>
            </Card>
            {/* 2. Block Samiti */}
            <Card className={`shadow-md ${isMobile ? "w-3/4 mx-auto" : ""}`}>
              <CardContent className={`${isMobile ? "p-3 h-[96px] flex flex-col items-center justify-center" : "p-4"} text-center`}>
                <h4 className="font-semibold">{t("block_samitis", lang)}</h4>
                <p className="text-2xl font-bold text-green-700">143</p>
              </CardContent>
            </Card>
            {/* 3. Gram Panchayat */}
            <Card className={`shadow-md ${isMobile ? "w-3/4 mx-auto" : ""}`}>
              <CardContent className={`${isMobile ? "p-3 h-[96px] flex flex-col items-center justify-center" : "p-4"} text-center`}>
                <h4 className="font-semibold">{t("level_gp", lang)}</h4>
                <p className="text-2xl font-bold text-green-700">6,222</p>
              </CardContent>
            </Card>
            {/* 4. Development Projects */}
            <Card className={`shadow-md ${isMobile ? "w-3/4 mx-auto" : ""}`}>
              <CardContent className={`${isMobile ? "p-3 h-[96px] flex flex-col items-center justify-center" : "p-4"} text-center`}>
                <h4 className="font-semibold">{t("development_projects", lang)}</h4>
                <p className="text-2xl font-bold text-green-700">12,540+</p>
              </CardContent>
            </Card>
            {!isMobile ? (
              <Card className="shadow-md">
                <CardContent className="p-4 text-center">
                  <h4 className="font-semibold">{t("registered_users", lang)}</h4>
                  <p className="text-2xl font-bold text-green-700">5.2L+</p>
                </CardContent>
              </Card>
            ) : null}
          </div>
          <p className="text-[15px] text-gray-600 mt-3">
            <span className="font-semibold">{t("data_source_label", lang)}:</span> {t("data_source_value", lang)}
          </p>
        </div>
      </section>

      {/* Footer moved to global SiteFooter */}
    </div>
  )
}
